import { useEffect, useState } from 'react';

import type { Manifest } from '../services/types';

import { Button } from './Button';
import { JsonEditor } from './JsonEditor';

export interface ManifestPreviewProps {
  manifest: Manifest;
  draft?: Manifest;
  onDraftChange?: (next: Manifest) => void;
  onSave?: (next: Manifest) => Promise<void> | void;
  onDiscard?: () => void;
  /** Triggers a rerun using the saved (not draft) manifest. */
  onRerun?: () => void;
}

/**
 * MOA manifest preview + edit surface.
 *
 * The top half is a human-readable view of the execution sequence and chunk
 * plan; the bottom half is the raw JSON editor for reviewers who need a
 * surgical change (swap a param, retarget a column, etc).
 */
export function ManifestPreview({
  manifest,
  draft,
  onDraftChange,
  onSave,
  onDiscard,
  onRerun,
}: ManifestPreviewProps) {
  const active = draft ?? manifest;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => setSaveError(null), [draft]);

  const totalRows = active.data_chunks.reduce((sum, c) => sum + (c.row_range[1] - c.row_range[0]), 0);

  const save = async () => {
    if (!onSave || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="manifest-title" className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 id="manifest-title" className="m-0">
            Execution manifest
          </h2>
          {draft && (
            <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-warn/15 text-warn">
              draft
            </span>
          )}
        </div>
        <p className="text-muted">
          {active.execution_sequence.length} step{active.execution_sequence.length === 1 ? '' : 's'} ·{' '}
          {active.data_chunks.length} chunk{active.data_chunks.length === 1 ? '' : 's'} ·{' '}
          {totalRows.toLocaleString()} row{totalRows === 1 ? '' : 's'} planned
        </p>
      </div>

      <div>
        <h3 className="mb-3">Execution sequence</h3>
        <ol className="space-y-2">
          {active.execution_sequence.map((step, i) => (
            <li
              key={i}
              className="rounded-md border border-border bg-paper px-4 py-3 flex items-start gap-4"
            >
              <span className="text-xs font-mono text-muted pt-0.5 w-6 flex-shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <code className="font-semibold text-ink">{step.agent_id}</code>
                  <span className="text-xs text-muted">→</span>
                  <span className="text-sm">{step.target_columns.join(', ')}</span>
                  {step.output_column && (
                    <span className="text-xs text-muted">
                      → writes <code>{step.output_column}</code>
                    </span>
                  )}
                </div>
                {step.params && Object.keys(step.params).length > 0 && (
                  <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted">
                    {Object.entries(step.params).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <dt>{k}:</dt>
                        <dd className="font-mono text-ink">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div>
        <h3 className="mb-3">Data chunks</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {active.data_chunks.map((c) => (
            <li key={c.chunk_id} className="rounded-md border border-border px-3 py-2 flex items-baseline justify-between">
              <code className="text-ink font-medium">{c.chunk_id}</code>
              <span className="text-muted text-xs">
                {c.table_id} · rows {c.row_range[0]}–{c.row_range[1]}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <JsonEditor
          label="Raw manifest JSON"
          value={active}
          onApply={(next) => onDraftChange?.(next as Manifest)}
          onReset={() => manifest}
          heightRem={18}
        />
      </div>

      <div className="flex items-center gap-3 pt-2 flex-wrap">
        {draft && (
          <>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save draft'}
            </Button>
            <Button variant="secondary" onClick={onDiscard} disabled={saving}>
              Discard changes
            </Button>
          </>
        )}
        {onRerun && (
          <Button variant="secondary" onClick={onRerun} disabled={Boolean(draft)}>
            Rerun pipeline
          </Button>
        )}
        {saveError && (
          <span role="alert" className="text-sm text-danger">
            {saveError}
          </span>
        )}
      </div>
    </section>
  );
}
